/**
 * App Bootstrap Service
 *
 * Handles critical initialization tasks on app load:
 * 1. CSRF cookie bootstrap
 * 2. User context (facilities, default facility)
 * 3. Facility ID resolution from route
 *
 * Call `bootstrapApp()` once on app mount and on facility route changes.
 */

import { apiClient } from './api';

// ============================================================================
// Constants
// ============================================================================

// Hard timeout for individual bootstrap API calls (CSRF, user context).
// Prevents indefinite hang on mobile networks with half-open connections.
const BOOTSTRAP_CALL_TIMEOUT_MS = 10_000;

// ============================================================================
// State Management
// ============================================================================

let bootstrapState = {
  csrfReady: false,
  userContext: null,
  activeFacilityId: null,
  bootstrapPromise: null,
  inProgress: false,
  lastFailureTime: 0,       // timestamp of last bootstrap failure
  consecutiveFailures: 0,   // count of consecutive failures for backoff
};

// Minimum cooldown (seconds) before retrying bootstrap after a failure.
// Doubles on each consecutive failure: 10 → 20 → 40 → 60 (cap).
const BOOTSTRAP_BASE_COOLDOWN_S = 10;
const BOOTSTRAP_MAX_COOLDOWN_S = 60;

// Listeners for facility ID changes
const facilityChangeListeners = new Set();

/**
 * Subscribe to facility ID changes
 * @param {Function} callback - Called with new facilityId
 * @returns {Function} Unsubscribe function
 */
export function onFacilityChange(callback) {
  facilityChangeListeners.add(callback);
  return () => facilityChangeListeners.delete(callback);
}

/**
 * Notify all listeners of facility ID change
 */
function notifyFacilityChange(facilityId) {
  console.debug('[Bootstrap] Notifying facility change:', facilityId);
  facilityChangeListeners.forEach(cb => {
    try {
      cb(facilityId);
    } catch (err) {
      console.error('[Bootstrap] Listener error:', err);
    }
  });
}

// ============================================================================
// CSRF Bootstrap
// ============================================================================

/**
 * Ensure CSRF cookie exists by calling /api/csrf/
 * @returns {Promise<boolean>} True if cookie now exists
 */
async function ensureCsrfCookie() {
  // Check if cookie already exists
  const hasCsrf = document.cookie.split(';').some(c =>
    c.trim().startsWith('csrftoken=')
  );

  if (hasCsrf) {
    console.debug('[Bootstrap] CSRF cookie already present');
    return true;
  }

  console.log('[Bootstrap] Fetching CSRF cookie from /api/csrf/');

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), BOOTSTRAP_CALL_TIMEOUT_MS);

  try {
    await apiClient.get('/csrf/', {
      _skipBootstrap: true,
      signal: controller.signal,
    });

    // Verify cookie is now present
    const hasCsrfAfter = document.cookie.split(';').some(c =>
      c.trim().startsWith('csrftoken=')
    );

    if (hasCsrfAfter) {
      console.warn('[Bootstrap] CSRF cookie obtained');
      return true;
    } else {
      console.error('[Bootstrap] CSRF cookie still missing after /api/csrf/ call');
      return false;
    }
  } catch (error) {
    if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
      console.error(`[Bootstrap] CSRF fetch timed out after ${BOOTSTRAP_CALL_TIMEOUT_MS}ms`);
    } else {
      console.error('[Bootstrap] Failed to fetch CSRF cookie:', error);
    }
    return false;
  } finally {
    clearTimeout(timer);
  }
}

// ============================================================================
// User Context
// ============================================================================

/**
 * Fetch user context from /api/me/context/
 * Returns user info, facilities, and default facility ID
 * @returns {Promise<Object|null>} User context or null
 */
async function fetchUserContext() {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), BOOTSTRAP_CALL_TIMEOUT_MS);

  try {
    const response = await apiClient.get('/me/context/', {
      _skipBootstrap: true,
      signal: controller.signal,
    });

    const context = response.data;
    console.warn('[Bootstrap] User context loaded:', {
      user: context.user?.email,
      facilitiesCount: context.facilities?.length || 0,
      defaultFacility: context.default_facility_id
    });

    return context;
  } catch (error) {
    if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
      console.error(`[Bootstrap] User context fetch timed out after ${BOOTSTRAP_CALL_TIMEOUT_MS}ms`);
      return null;
    }
    // 401/403 means not logged in - that's okay
    if (error.response?.status === 401 || error.response?.status === 403) {
      console.debug('[Bootstrap] Not authenticated, skipping user context');
      return null;
    }

    console.error('[Bootstrap] Failed to fetch user context:', error);
    return null;
  } finally {
    clearTimeout(timer);
  }
}

// ============================================================================
// Facility ID Resolution
// ============================================================================

/**
 * Extract facility UUID from URL pathname
 * Matches: /facilities/{uuid}/... or /facility/{uuid}/...
 * @param {string} pathname - window.location.pathname or custom path
 * @returns {string|null} Facility UUID or null
 */
export function extractFacilityIdFromUrl(pathname = window.location?.pathname) {
  if (!pathname) return null;

  // Match facility ID in various route patterns:
  // - /facility/:id/ or /facilities/:id/
  // - /my-store/:id/
  // - /facilities/:id/store or similar
  const match = pathname.match(/\/(?:facilit(?:y|ies)|my-store)\/([a-f0-9-]{36})(?:\/|$)/i);
  return match ? match[1] : null;
}

/**
 * Resolve active facility ID using priority cascade:
 * 1. UUID in current URL (if user has access)
 * 2. default_facility_id from /api/me/context/
 * 3. localStorage.getItem('activeFacilityId')
 * 4. First facility from user context
 *
 * @param {Object} userContext - User context from /api/me/context/
 * @returns {string|null} Facility UUID or null
 */
export function resolveFacilityId(userContext) {
  // Priority 1: URL route UUID (if valid for user)
  const urlFacilityId = extractFacilityIdFromUrl();
  if (urlFacilityId && userContext?.facilities) {
    const hasAccess = userContext.facilities.some(f => f.id === urlFacilityId);
    if (hasAccess) {
      console.debug('[Bootstrap] Using URL facility:', urlFacilityId);
      return urlFacilityId;
    }
  }

  // Priority 2: Default from backend
  if (userContext?.default_facility_id) {
    console.debug('[Bootstrap] Using backend default facility:', userContext.default_facility_id);
    return userContext.default_facility_id;
  }

  // Priority 3: localStorage (may be stale)
  const storedId = localStorage.getItem('activeFacilityId');
  if (storedId && userContext?.facilities) {
    const hasAccess = userContext.facilities.some(f => f.id === storedId);
    if (hasAccess) {
      console.debug('[Bootstrap] Using localStorage facility:', storedId);
      return storedId;
    }
  }

  // Priority 4: First facility
  if (userContext?.facilities?.length > 0) {
    const firstId = userContext.facilities[0].id;
    console.debug('[Bootstrap] Using first facility:', firstId);
    return firstId;
  }

  return null;
}

// ============================================================================
// Main Bootstrap
// ============================================================================

/**
 * Bootstrap application on load or route change
 * - Ensures CSRF cookie exists
 * - Fetches user context (facilities, default facility)
 * - Resolves active facility ID
 * - Syncs to localStorage
 *
 * Safe to call multiple times (singleton pattern)
 * @param {boolean} force - Force re-bootstrap even if already done
 * @returns {Promise<Object>} Bootstrap state
 */
export async function bootstrapApp(force = false) {
  // If bootstrap in progress, wait for it
  if (bootstrapState.inProgress && bootstrapState.bootstrapPromise) {
    console.debug('[Bootstrap] Already in progress, waiting...');
    return bootstrapState.bootstrapPromise;
  }

  // If already bootstrapped and not forcing, return cached state
  if (!force && bootstrapState.csrfReady && bootstrapState.userContext !== undefined) {
    console.debug('[Bootstrap] Using cached state');
    return Promise.resolve({
      csrfReady: bootstrapState.csrfReady,
      userContext: bootstrapState.userContext,
      activeFacilityId: bootstrapState.activeFacilityId
    });
  }

  // Cooldown after failure — prevent self-DDoS when API/edge is rate-limiting
  if (!force && bootstrapState.lastFailureTime > 0) {
    const cooldownS = Math.min(
      BOOTSTRAP_BASE_COOLDOWN_S * Math.pow(2, bootstrapState.consecutiveFailures - 1),
      BOOTSTRAP_MAX_COOLDOWN_S
    );
    const elapsed = (Date.now() - bootstrapState.lastFailureTime) / 1000;
    if (elapsed < cooldownS) {
      console.warn(`[Bootstrap] Cooling down (${Math.ceil(cooldownS - elapsed)}s remaining)`);
      return Promise.resolve({
        csrfReady: bootstrapState.csrfReady,
        userContext: bootstrapState.userContext,
        activeFacilityId: bootstrapState.activeFacilityId
      });
    }
  }

  const t0 = Date.now();
  console.warn('[Bootstrap] Starting app bootstrap...', {
    build: process.env.REACT_APP_GIT_COMMIT || 'dev',
    route: window.location.pathname,
    hasAuthToken: !!localStorage.getItem('authToken'),
    ua: navigator.userAgent,
  });
  bootstrapState.inProgress = true;

  bootstrapState.bootstrapPromise = (async () => {
    try {
      // Step 1: CSRF cookie
      const csrfReady = await ensureCsrfCookie();
      bootstrapState.csrfReady = csrfReady;

      // Step 2: User context
      const userContext = await fetchUserContext();
      bootstrapState.userContext = userContext;

      // Step 3: Resolve facility ID
      const facilityId = resolveFacilityId(userContext);

      // Step 4: Persist and notify if changed
      if (facilityId !== bootstrapState.activeFacilityId) {
        bootstrapState.activeFacilityId = facilityId;

        if (facilityId) {
          localStorage.setItem('activeFacilityId', facilityId);
        } else {
          localStorage.removeItem('activeFacilityId');
        }

        notifyFacilityChange(facilityId);
      }

      // Reset failure tracking on success
      bootstrapState.lastFailureTime = 0;
      bootstrapState.consecutiveFailures = 0;

      console.warn('[Bootstrap] Complete', {
        csrf: csrfReady,
        authenticated: !!userContext?.user,
        facilities: userContext?.facilities?.length || 0,
        activeFacility: facilityId,
        durationMs: Date.now() - t0,
      });

      return {
        csrfReady,
        userContext,
        activeFacilityId: facilityId
      };
    } catch (error) {
      bootstrapState.consecutiveFailures += 1;
      bootstrapState.lastFailureTime = Date.now();
      console.error(`[Bootstrap] Failed (attempt ${bootstrapState.consecutiveFailures}, ${Date.now() - t0}ms):`, error);
      throw error;
    } finally {
      bootstrapState.inProgress = false;
      bootstrapState.bootstrapPromise = null;
    }
  })();

  return bootstrapState.bootstrapPromise;
}

/**
 * Update active facility ID (e.g., when user switches via dropdown)
 * @param {string} facilityId - New facility UUID
 */
export function setActiveFacilityId(facilityId) {
  if (facilityId === bootstrapState.activeFacilityId) {
    return; // No change
  }

  console.log('[Bootstrap] Setting active facility:', facilityId);

  bootstrapState.activeFacilityId = facilityId;

  if (facilityId) {
    localStorage.setItem('activeFacilityId', facilityId);
  } else {
    localStorage.removeItem('activeFacilityId');
  }

  notifyFacilityChange(facilityId);
}

/**
 * Get current active facility ID
 * @returns {string|null} Current facility UUID
 */
export function getActiveFacilityId() {
  return bootstrapState.activeFacilityId;
}

/**
 * Get cached user context
 * @returns {Object|null} User context or null
 */
export function getUserContext() {
  return bootstrapState.userContext;
}

/**
 * Check if CSRF cookie is ready
 * @returns {boolean} True if CSRF cookie exists
 */
export function isCsrfReady() {
  return bootstrapState.csrfReady;
}
